import { ApolloClient, NormalizedCacheObject, from, HttpLink, ApolloLink } from '@apollo/client';
import type { NetworkError } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import globalState from '../operations/commands';
import { cache } from '../cache';
import { configureScope, captureException } from '@sentry/browser';

const apiType = ['gql', 'rest'] as const;
type ApiType = (typeof apiType)[number];
function hasResult(
  error: any,
): error is NetworkError & { result: { code?: string; error: { message: string } }; statusCode: number } {
  return !!(error.result && error.result?.error?.message && error.statusCode);
}

export type LinkArgs = {
  [x: string]: {
    type: ApiType;
    uri: string;
    credentials?: RestLink.Options['credentials'] | string;
    headers?: RestLink.Options['headers'];
    useCustomFetch?: boolean;
    responseTransformer?: (response: any) => Promise<any>;
    bodySerializers?: Record<string, <D, H>(data: D, headers: H) => any>;
  };
};

const customFetch = (uri: string, options: any) => {
  const { operationName } = JSON.parse(options.body);
  return fetch(`${uri}/${operationName}`, options);
};

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  configureScope((scope) => {
    scope.setExtras({ where: 'error from apollo client' });
    // scope.setUser({
    //   id: ,
    //   username: ,
    //   email:,
    // });
  });
  if (graphQLErrors) {
    /* individual errors would be better handled by the calling functions/components
    graphQLErrors.map(({ message, name, ...rest }) => {
      globalState.setErrors({
        message,
        name: name ?? 'GraphQLError',
        ...rest,
        clientName: operation.getContext().clientName,
      });
    });
    */
  }
  if (networkError) {
    if (hasResult(networkError)) {
      // Only message gets passed on to ApolloError in QueryOption['onError']. need to override message if partial data from Rest API is returned
      // https://github.com/apollographql/apollo-client/blob/b214dd1d31cdfe6b5477bb5887a7d9615a302b27/src/errors/index.ts#L32
      networkError.message = networkError.result.code ?? networkError.result.error.message;
      console.log(networkError, networkError.statusCode);
      if (networkError.statusCode === 401) {
        globalState.setErrors({
          name: 'VerificationNeeded',
          status: networkError.statusCode,
          message: networkError.message,
        });
        captureException(Error(networkError.message));
      }
      if (networkError.statusCode === 500) {
        globalState.setErrors({
          name: 'UnimportantError',
          status: networkError.statusCode,
          message: 'サーバーエラー', // networkError.message,
          context: operation.getContext().clientName,
        });
        captureException(Error(networkError.message));
      }
    }
  }
});

const initApolloClient = (args: LinkArgs): ApolloClient<NormalizedCacheObject> => {
  const chainedLinks = Object.entries(args).reduce((previous: ApolloLink | null, current: [string, any]) => {
    const [key, value] = current;
    const keyLink =
      value.type === 'gql'
        ? new HttpLink({
            uri: value.uri,
            credentials: value.credentials,
            headers: value.headers,
            ...(value.useCustomFetch ? { fetch: customFetch } : {}),
          })
        : new RestLink({
            uri: value.uri,
            credentials: value.credentials,
            headers: value.headers,
            responseTransformer: value.responseTransformer,
            bodySerializers: value.bodySerializers,
          });

    if (!previous) return keyLink;
    return ApolloLink.split((operation) => operation.getContext().clientName === key, keyLink, previous);
  }, null);

  return new ApolloClient({
    link: from([errorLink, chainedLinks!]),
    cache,
  });
};

export default initApolloClient;
